import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useParams } from 'react-router-dom';
import { Mail, MapPin, ChevronDown, Award, ShieldCheck, Send, Menu, X, CheckCircle, ArrowLeft, Users } from 'lucide-react';
import './App.css';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// --- SHARED DATA ---
const coreTeamData = [
  {
    name: "M. Srinivasa Rao",
    role: "President",
    slug: "president",
    img: "/Srinivasarao.jpeg",
    bio: "M. Srinivasa Rao is the founding President of AAAP, a visionary leader with decades of experience in managing autonomous engineering institutions across Andhra Pradesh. His commitment to academic excellence and institutional governance has been the cornerstone of the association's formation and growth.",
    responsibilities: [
      "Chair all General Body and Executive Committee meetings",
      "Represent AAAP before Government, UGC, AICTE, and Universities",
      "Sign and authenticate all official correspondence",
      "Provide strategic direction and policy oversight for the association"
    ]
  },
  {
    name: "Dr. V. Jaya Chandra Reddy",
    role: "General Secretary",
    slug: "general-secretary",
    img: "/secretary.jpeg",
    bio: "Dr. V. Jaya Chandra Reddy brings distinguished academic expertise and administrative excellence to his role as General Secretary. With a long career in technical education and institution management, he oversees the day-to-day administration and inter-institutional coordination of AAAP.",
    responsibilities: [
      "Maintain records and minutes of all General Body and Executive meetings",
      "Coordinate with UGC, AICTE, and affiliated Universities on behalf of AAAP",
      "Manage all official communications, circulars, and notifications",
      "Oversee day-to-day administrative operations of the association"
    ]
  },
  {
    name: "V.V. Nageswara Rao",
    role: "Treasurer",
    slug: "treasurer",
    img: "/treasurer.jpeg",
    bio: "V.V. Nageswara Rao serves as Treasurer of AAAP, responsible for the financial management and fiscal oversight of the association. His prudent financial stewardship ensures that the association's resources are managed with full transparency, accountability, and compliance.",
    responsibilities: [
      "Maintain all financial records, accounts, and ledgers of the association",
      "Prepare the annual budget proposals and financial statements",
      "Manage collection of membership fees and dues from member institutions",
      "Ensure financial compliance and readiness for statutory audits"
    ]
  }
];

const allMembers = [
  ...coreTeamData.map(m => ({ name: m.name, role: m.role, img: m.img, slug: m.slug })),
  { name: "G.V.M. Mohan Kumar", role: "Vice President", img: "/vicepresident1.jpeg" },
  { name: "M. Srinivasa Babu", role: "Vice President", img: "/vicepresident2.jpeg" },
  { name: "B. Kanakadurga", role: "Joint Secretary", img: "/jointsecretary.jpeg" },
  { name: "S. Kirti Kumar", role: "Joint Secretary", img: "/jointsecretary1.jpeg" }
];

// --- SHARED CONTENT ---
const FullName = () => (
  <>
    <span className="brand-hl">A</span>ssociation of{' '}
    <span className="brand-hl">A</span>utonomous Engineering College Management's of{' '}
    <span className="brand-hl">A</span>ndhra <span className="brand-hl">P</span>radesh
  </>
);
const ADDRESS_LINES = [
  "Door No. 5-8-740, First Floor, Vishal Residency,",
  "NTR Road, Padmaja Nagar, Revenue Ward-5,",
  "Opp. VR Siddhartha Engineering College Main Gate,",
  "Tadigadapa, Krishna District — 521137, Andhra Pradesh."
];
const AIMS = [
  { title: "Technical Education", desc: "Promote the cause of Technical Education with specific focus on the initiatives of Autonomous Engineering College Managements in Andhra Pradesh." },
  { title: "Unified Coordination", desc: "Serve as a unified coordinating body representing member colleges before the Government of India, UGC, AICTE, the Director of Technical Education, and various Universities on all regulatory and operational challenges." },
  { title: "Professional Relations", desc: "Foster professional and cordial relations between member managements and all statutory authorities and governing bodies." },
  { title: "Institutional Excellence", desc: "Ensure the smooth and efficient running of institutions by promoting healthy relationships between Managements, staff, students, and parents." },
  { title: "Collaboration", desc: "Collaborate with regional, national, or international associations that share similar objectives." },
  { title: "Governance", desc: "Frame, amend, and implement rules and regulations necessary for the efficient governance of the Association." },
  { title: "Lawful Activities", desc: "Undertake all lawful acts incidental or conducive to the attainment of the aforementioned objects." },
];

// --- HOME PAGE ---
const Home = () => (
  <div className="fade-in">
    <header className="hero-banner">
      <div className="hero-overlay">
        <p className="hero-eyebrow">Official Representative Body</p>
        <h1>Welcome to <span className="hero-accent">AAAP</span></h1>
        <h2><FullName /></h2>
        <p className="reg-tag">Registered Under the AP Societies Registration Act, 2001 — No: 110 of 2026</p>
        <div className="hero-actions">
          <Link to="/about" className="hero-btn-primary">Know More</Link>
          <Link to="/contact" className="hero-btn-outline">Contact Us</Link>
        </div>
      </div>
    </header>

    {/* Stats band — sits cleanly between hero and content */}
    <div className="stats-band">
      <div className="stats-band-inner">
        <div className="hero-stat">
          <strong>Est. 2026</strong>
          <span>Founded</span>
        </div>
        <div className="hero-stat-sep" />
        <div className="hero-stat">
          <strong>Andhra Pradesh</strong>
          <span>Area of Activity</span>
        </div>
        <div className="hero-stat-sep" />
        <div className="hero-stat">
          <strong>Regd. No. 110</strong>
          <span>AP Societies Act, 2001</span>
        </div>
        <div className="hero-stat-sep" />
        <div className="hero-stat">
          <strong>7 Core Aims</strong>
          <span>Objectives</span>
        </div>
      </div>
    </div>

    <div className="content-section">
      {/* Association identity */}
      <div className="intro-card">
        <div className="intro-icon-row">
          <img src="/aaap3.png" alt="AAAP" className="intro-logo" />
        </div>
        <p className="intro-assoc-name"><FullName /></p>
        <p>
          The official representative body of Autonomous Engineering College Managements across Andhra Pradesh —
          dedicated to unified advocacy, institutional excellence, and collaborative governance.
        </p>
      </div>

      {/* Area of Activity */}
      <div className="activity-banner">
        <ShieldCheck size={22} className="icon-green" />
        <p><strong>Area of Activity:</strong> The activities of the Association shall extend to all places anywhere in Andhra Pradesh which the Association resolves.</p>
      </div>

      {/* Aims & Objectives */}
      <div className="section-title">
        <Award className="icon-green" />
        <h3>Aims &amp; Objectives</h3>
      </div>
      <div className="aims-section-wrap">
        <div className="aims-list">
          {AIMS.map((aim, i) => (
            <div key={i} className="aim-item" style={{ animationDelay: `${i * 0.07}s` }}>
              <span className="aim-number">{String(i + 1).padStart(2, '0')}</span>
              <div className="aim-body">
                <strong>{aim.title}</strong>
                <p>{aim.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Leadership preview */}
      <div className="home-team-preview">
        <div className="section-title">
          <Users className="icon-green" />
          <h3>Our Leadership</h3>
        </div>
        <div className="home-team-grid">
          {coreTeamData.map((m, i) => (
            <Link key={i} to={`/core/${m.slug}`} className="home-team-card">
              <div className="home-team-photo"><img src={m.img} alt={m.name} /></div>
              <strong>{m.name}</strong>
              <span>{m.role}</span>
            </Link>
          ))}
        </div>
        <div className="home-team-cta">
          <Link to="/core-team" className="btn-primary">Meet the Full Core Team</Link>
          <Link to="/members" className="btn-outline">View Executive Committee</Link>
        </div>
      </div>
    </div>
  </div>
);

// --- ABOUT PAGE ---
const About = () => (
  <div className="fade-in about-page">
    <div className="page-header">
      <h1>About AAAP</h1>
      <p>Leading and Unifying Autonomous Engineering Excellence in Andhra Pradesh</p>
    </div>
    <div className="about-content">
      <div className="about-text-block">
        <ShieldCheck size={44} className="icon-blue" />
        <div>
          <p style={{ margin: '0 0 12px 0' }}>
            <strong><FullName /></strong> is a premier representative body registered under the
            AP Societies Registration Act, 2001 (No: 110 of 2026), dedicated to the cause of Technical Education
            with a specific focus on Autonomous Engineering College Managements across Andhra Pradesh.
          </p>
          <p style={{ margin: 0 }}>
            <strong>Area of Activity:</strong> The activities of the Association shall extend to all places
            anywhere in Andhra Pradesh which the Association resolves.
          </p>
        </div>
      </div>

      <div className="section-title" style={{ marginTop: '10px' }}>
        <Award className="icon-blue" />
        <h3 style={{ color: 'var(--news-blue)' }}>Aims &amp; Objectives</h3>
      </div>
      <div className="objectives-container">
        {AIMS.map((item, idx) => (
          <div key={idx} className="glass-card">
            <h4>{item.title}</h4>
            <p>{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// --- CORE TEAM PAGE ---
const CoreTeam = () => (
  <div className="fade-in">
    <div className="page-header">
      <h1>Core Team</h1>
      <p>The leadership driving AAAP's vision and mission</p>
    </div>
    <div className="coreteam-section">
      <div className="coreteam-grid">
        {coreTeamData.map((member, i) => (
          <div key={i} className="coreteam-card">
            <div className="ct-photo-frame">
              <img src={member.img} alt={member.name} />
            </div>
            <div className="ct-card-body">
              <span className="ct-role-badge">{member.role}</span>
              <h3>{member.name}</h3>
              <p className="ct-bio-preview">
                {member.bio.length > 130 ? member.bio.substring(0, 130) + '…' : member.bio}
              </p>
              <Link to={`/core/${member.slug}`} className="ct-view-btn">View Profile</Link>
            </div>
          </div>
        ))}
      </div>
      <div className="coreteam-footer-link">
        <Link to="/members" className="btn-outline">View Full Executive Committee</Link>
      </div>
    </div>
  </div>
);

// --- INDIVIDUAL PROFILE PAGE ---
const ProfilePageWrapper = () => {
  const { slug } = useParams();
  const member = coreTeamData.find(m => m.slug === slug);

  if (!member) return (
    <div className="fade-in">
      <div className="page-header"><h1>Profile Not Found</h1><p>The requested profile does not exist.</p></div>
      <div className="content-section" style={{ textAlign: 'center', paddingTop: '40px' }}>
        <Link to="/core-team" className="btn-primary">Back to Core Team</Link>
      </div>
    </div>
  );

  return (
    <div className="fade-in">
      <div className="page-header profile-header-bg">
        <p className="profile-breadcrumb">Core Team</p>
        <h1>{member.name}</h1>
        <p>{member.role}, AAAP</p>
      </div>
      <div className="profile-section">
        <div className="profile-layout">
          <div className="profile-photo-col">
            <div className="profile-photo-frame">
              <img src={member.img} alt={member.name} />
            </div>
            <div className="profile-name-card">
              <h2>{member.name}</h2>
              <span className="profile-role-badge">{member.role}</span>
              <Link to="/core-team" className="profile-back-btn">
                <ArrowLeft size={15} /> Back to Core Team
              </Link>
            </div>
          </div>
          <div className="profile-details-col">
            <div className="profile-bio-card">
              <h3>About</h3>
              <p>{member.bio}</p>
            </div>
            <div className="profile-resp-card">
              <h3>Key Responsibilities</h3>
              <ul className="resp-list">
                {member.responsibilities.map((r, i) => (
                  <li key={i}>
                    <CheckCircle size={16} className="icon-green" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- MEMBERS PAGE (Full Executive Committee) ---
const Members = () => (
  <div className="fade-in">
    <div className="page-header">
      <h1>Executive Committee</h1>
      <p>Entrusted with the management and affairs of the association</p>
    </div>
    <div className="member-grid-premium">
      {allMembers.map((m, i) => (
        <div key={i} className="member-card-premium">
          {m.slug ? (
            <Link to={`/core/${m.slug}`} className="member-card-link">
              <div className="img-frame"><img src={m.img} alt={m.name} /></div>
              <div className="member-info">
                <h3>{m.name}</h3>
                <p className="role-tag">{m.role}</p>
                <span className="member-view-link">View Profile →</span>
              </div>
            </Link>
          ) : (
            <>
              <div className="img-frame"><img src={m.img} alt={m.name} /></div>
              <div className="member-info">
                <h3>{m.name}</h3>
                <p className="role-tag">{m.role}</p>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  </div>
);

// --- NEWS PAGE ---
const News = () => (
  <div className="fade-in news-page">
    <div className="page-header news-header-bg">
      <h1>Latest News & Updates</h1>
      <p>Stay informed about AAAP activities and announcements</p>
    </div>
    <div className="content-section">
      <article className="news-card">
        <div className="news-date">APR 2026</div>
        <div className="news-body">
          <h3>Annual General Body Meeting</h3>
          <p>Scheduled for April 2026. All member institutions are invited to attend and review the annual report of the association.</p>
        </div>
      </article>
    </div>
  </div>
);

// --- CONTACT PAGE ---
const Contact = () => (
  <div className="fade-in contact-page">
    <div className="page-header contact-header-bg">
      <h1>Contact Us</h1>
      <p>Connect with our Registered Office in Tadigadapa</p>
    </div>
    <div className="contact-grid-container">
      <div className="contact-details-panel">
        <h2 className="section-heading-alt">Office Location</h2>
        <div className="contact-item">
          <MapPin className="icon-alt" />
          <div>
            <strong>Registered Office</strong>
            <p>{ADDRESS_LINES.map((line, i) => <span key={i}>{line}<br /></span>)}</p>
          </div>
        </div>
        <div className="contact-item">
          <Mail className="icon-alt" />
          <div>
            <strong>Email</strong>
            <p>office@aaap.co.in</p>
          </div>
        </div>
        <div className="map-embed">
          <iframe title="AAAP Map" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3826.666!2d80.688!3d16.488!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTbCsDI5JzE2LjgiTiA4MMKwNDEnMTYuOCJF!5e0!3m2!1sen!2sin!4v1650000000000" width="100%" height="250" loading="lazy"></iframe>
        </div>
      </div>
      <div className="contact-form-panel">
        <h2 className="section-heading-alt">Send a Message</h2>
        <form className="premium-form" onSubmit={(e) => e.preventDefault()}>
          <input type="text" placeholder="Full Name" required />
          <input type="email" placeholder="Email Address" required />
          <input type="text" placeholder="Subject" required />
          <textarea placeholder="Your Message" rows="4" required></textarea>
          <button type="submit" className="btn-alt"><Send size={18} /> Send Message</button>
        </form>
      </div>
    </div>
  </div>
);

// --- COMMUNICATIONS PAGE ---
const Communications = () => (
  <div className="fade-in">
    <div className="page-header">
      <h1>Communications</h1>
      <p>Official correspondence and announcements from AAAP</p>
    </div>
    <div className="content-section">
      <div className="intro-card">
        <p>Official communications, circulars, and announcements will be published here.</p>
      </div>
    </div>
  </div>
);

// --- FOOTER ---
const Footer = () => (
  <footer className="main-footer">
    <div className="footer-content">
      <div className="footer-brand">
        <div className="footer-brand-top">
          <img src="/aaap3.png" alt="AAAP Logo" className="footer-logo" />
          <p className="footer-fullname"><FullName /></p>
        </div>
        <p className="footer-reg">Regd. No: 110 of 2026 | AP Societies Registration Act, 2001</p>
      </div>
      <div className="footer-links">
        <h4>Quick Links</h4>
        <Link to="/about">About</Link>
        <Link to="/core-team">Core Team</Link>
        <Link to="/members">Members</Link>
        <Link to="/news">News</Link>
        <Link to="/contact">Contact Us</Link>
      </div>
      <div className="footer-info">
        <h4>Registered Office</h4>
        <div className="footer-address">
          <MapPin size={14} />
          <address>
            {ADDRESS_LINES.map((line, i) => <span key={i}>{line}<br /></span>)}
          </address>
        </div>
        <p><Mail size={14} /> office@aaap.co.in</p>
      </div>
    </div>
    <div className="footer-bottom">© 2026 AAAP. All Rights Reserved. Professional Academic Representation across Andhra Pradesh.</div>
  </footer>
);

// --- MAIN APP ---
export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  return (
    <Router>
      <ScrollToTop />

      {/* Mobile overlay */}
      {menuOpen && <div className="drawer-overlay" onClick={closeMenu} />}

      {/* Mobile slide drawer */}
      <div className={`mobile-drawer ${menuOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          <div className="logo-wrapper">
            <img src="/aaap3.png" alt="AAAP Logo" className="nav-logo" />
          </div>
          <div className="brand-text">
            <span className="brand-fullname">
              <span className="brand-hl">A</span>ssociation of{' '}
              <span className="brand-hl">A</span>utonomous Engineering College<br />
              Management's of{' '}
              <span className="brand-hl">A</span>ndhra{' '}
              <span className="brand-hl">P</span>radesh
            </span>
          </div>
          <button className="drawer-close" onClick={closeMenu}><X size={22} /></button>
        </div>
        <nav className="drawer-nav">
          <Link to="/" onClick={closeMenu}>Home</Link>
          <Link to="/about" onClick={closeMenu}>About</Link>
          <span className="drawer-group-label">Core Team</span>
          <Link to="/core-team" onClick={closeMenu} className="drawer-sub">All Core Team</Link>
          <Link to="/core/president" onClick={closeMenu} className="drawer-sub">President</Link>
          <Link to="/core/general-secretary" onClick={closeMenu} className="drawer-sub">General Secretary</Link>
          <Link to="/core/treasurer" onClick={closeMenu} className="drawer-sub">Treasurer</Link>
          <Link to="/members" onClick={closeMenu}>Members</Link>
          <Link to="/news" onClick={closeMenu}>News</Link>
          <Link to="/contact" onClick={closeMenu} className="drawer-cta">Contact Us</Link>
        </nav>
      </div>

      <nav className="modern-nav">
        <div className="nav-container">
          <Link to="/" className="brand-area">
            <div className="logo-wrapper">
              <img src="/aaap3.png" alt="AAAP Logo" className="nav-logo" />
            </div>
            <div className="brand-text">
              <span className="brand-fullname">
                <span className="brand-hl">A</span>ssociation of{' '}
                <span className="brand-hl">A</span>utonomous Engineering College<br />
                Management's of{' '}
                <span className="brand-hl">A</span>ndhra{' '}
                <span className="brand-hl">P</span>radesh
              </span>
            </div>
          </Link>
          <ul className="nav-menu">
            <li><Link to="/">Home</Link></li>
            <li><Link to="/about">About</Link></li>
            <li className="nav-dropdown">
              <span className="drop-label">Core Team <ChevronDown size={14} /></span>
              <div className="drop-menu">
                <Link to="/core/president">President</Link>
                <Link to="/core/general-secretary">General Secretary</Link>
                <Link to="/core/treasurer">Treasurer</Link>
                <div className="drop-divider" />
                <Link to="/core-team" className="drop-all">View All Core Team</Link>
              </div>
            </li>
            <li><Link to="/members">Members</Link></li>
            <li><Link to="/news">News</Link></li>
            <li><Link to="/contact" className="nav-cta">Contact Us</Link></li>
          </ul>
          <button className="hamburger-btn" onClick={() => setMenuOpen(true)} aria-label="Open menu">
            <Menu size={26} />
          </button>
        </div>
      </nav>

      <main className="app-main">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/core-team" element={<CoreTeam />} />
          <Route path="/core/:slug" element={<ProfilePageWrapper />} />
          <Route path="/members" element={<Members />} />
          <Route path="/news" element={<News />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/communications" element={<Communications />} />
        </Routes>
      </main>
      <Footer />
    </Router>
  );
}
